import { ZONE_IDS, ZoneId, zoneIdForTarget } from '../../helpers/goalTargetFrame';
import { POOL_ZONE_IDS, PoolZoneId, poolZoneIdForLocation } from '../../helpers/poolShotZones';
import { EventLightVm, MatchPlayerVm } from '../../utils/api';
import {
    GkSituation,
    GkSituationKey,
    matchesGkSituation,
    matchesSelectedSituations,
} from './insightsGoalkeeperZones';
import {
    buildScoreAtMomentByEventId,
    emptyMapSide,
    MapOutcome,
    mapPointContext,
    MapSide,
    matchFinalScoreLine,
    shotOutcome,
} from './insightsMapData';
import {
    MapPeriodKey,
    matchesPeriodFilter,
    nameByMatchPlayerId,
    playerDisplayName,
} from './insightsMatchStats';
import { isPlaceholderPlayerName } from './insightsPlayerSeason';
import {
    PLAYER_SHOT_SAMPLE_WARNING,
    shooterCellsToZoneCounts,
    ShooterPoolZoneCell,
    ShooterZoneCell,
    ShooterZoneStats,
    shootPctText,
} from './insightsPlayerShotZones';
import { MatchBundle } from './insightsQueries';
import { matchesShotKind, ShotKindFilter } from './insightsShotKindFilters';

export { shooterCellsToZoneCounts, shootPctText };

export type MatchShotSide = 'for' | 'against';

/** null/undefined = all players; [] = none; otherwise selected playerIds. */
export type MatchPlayerIdFilter = number[] | null | undefined;

export interface MatchMapPlayerOption {
    playerId: number;
    label: string;
}

export interface MatchMapShotOptions {
    situation?: GkSituation | GkSituationKey[];
    playerIds?: MatchPlayerIdFilter;
    /** null = all periods; [] = none. */
    periodKeys?: MapPeriodKey[] | null;
    /** Technique / direct-foul / fakes. null dims = all. */
    kind?: ShotKindFilter;
}

/** null = all; [] = none; otherwise selected normalized type keys. */
export type MatchExclusionTypeFilter = string[] | null | undefined;

/** null = all; [] = none; otherwise exclusion.teamId values. */
export type MatchExclusionTeamFilter = number[] | null | undefined;

export interface MatchExclusionMapOptions {
    playerIds?: MatchPlayerIdFilter;
    periodKeys?: MapPeriodKey[] | null;
    typeKeys?: MatchExclusionTypeFilter;
    teamIds?: MatchExclusionTeamFilter;
}

const normalizeExclusionTypeKey = (type?: string): string => {
    const key = (type || 'exclusion').toLowerCase().replace(/[\s_-]/g, '');
    if (key === 'penaltyshootout') return 'penalty';
    return key || 'exclusion';
};

const formatExclusionTypeLabel = (key: string): string => {
    if (key === 'exclusion') return 'Exclusion';
    if (key === 'suspension') return 'Suspension';
    if (key === 'brutality') return 'Brutality';
    if (key === 'penalty') return 'Penalty';
    return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const allowsType = (type: string | undefined, filter: MatchExclusionTypeFilter): boolean => {
    if (filter == null) return true;
    if (filter.length === 0) return false;
    return filter.includes(normalizeExclusionTypeKey(type));
};

const allowsTeam = (teamId: number | undefined, filter: MatchExclusionTeamFilter): boolean => {
    if (filter == null) return true;
    if (filter.length === 0) return false;
    if (teamId == null) return false;
    return filter.includes(teamId);
};

type TeamShot = {
    event: EventLightVm;
    bundleId: number;
    shooterPlayerId?: number;
};

const isPenaltyExclusion = (type?: string): boolean => {
    const key = (type || '').toLowerCase().replace(/[\s_-]/g, '');
    return key === 'penalty' || key === 'penaltyshootout';
};

const allowsPlayer = (playerId: number | undefined, filter: MatchPlayerIdFilter): boolean => {
    if (filter == null) return true;
    if (filter.length === 0) return false;
    if (playerId == null) return false;
    return filter.includes(playerId);
};

const indexLineup = (
    bundle: MatchBundle
): Map<number, { playerId: number; teamId: number; name: string; cap?: number }> => {
    const map = new Map<number, { playerId: number; teamId: number; name: string; cap?: number }>();
    (bundle.detail.players || []).forEach((mp: MatchPlayerVm) => {
        if (mp.id == null || mp.playerId == null || mp.teamId == null) return;
        map.set(mp.id, {
            playerId: mp.playerId,
            teamId: mp.teamId,
            name: playerDisplayName(mp),
            cap: mp.capNumber,
        });
    });
    return map;
};

const emptyGoalCells = (): ShooterZoneCell[] =>
    ZONE_IDS.map((zone) => ({ zone, shots: 0, goals: 0, misses: 0 }));

const emptyPoolCells = (): ShooterPoolZoneCell[] =>
    POOL_ZONE_IDS.map((zone) => ({ zone, shots: 0, goals: 0, misses: 0 }));

const bumpGoal = (cells: ShooterZoneCell[], zone: ZoneId, isGoal: boolean): void => {
    const cell = cells.find((c) => c.zone === zone);
    if (!cell) return;
    cell.shots += 1;
    if (isGoal) cell.goals += 1;
    else cell.misses += 1;
};

const bumpPool = (cells: ShooterPoolZoneCell[], zone: PoolZoneId, isGoal: boolean): void => {
    const cell = cells.find((c) => c.zone === zone);
    if (!cell) return;
    cell.shots += 1;
    if (isGoal) cell.goals += 1;
    else cell.misses += 1;
};

const shotsBySide = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    side: MatchShotSide,
    options: MatchMapShotOptions = {}
): TeamShot[] => {
    const found: TeamShot[] = [];
    const { situation, playerIds, periodKeys, kind } = options;

    bundles.forEach((bundle) => {
        const lineup = indexLineup(bundle);
        bundle.events.forEach((event: EventLightVm) => {
            if (!event.shot) return;
            if (!matchesPeriodFilter(event, periodKeys)) return;
            const ours = ourTeamIds.includes(event.shot.teamId);
            if (side === 'for' ? !ours : ours) return;

            if (Array.isArray(situation)) {
                if (!matchesSelectedSituations(event.shot, situation)) return;
            } else if (!matchesGkSituation(event.shot, situation)) {
                return;
            }
            if (!matchesShotKind(event.shot, kind)) return;

            const shooter =
                event.shot.takenById != null ? lineup.get(event.shot.takenById) : undefined;
            if (!allowsPlayer(shooter?.playerId, playerIds)) return;

            found.push({
                event,
                bundleId: bundle.detail.id,
                shooterPlayerId: shooter?.playerId,
            });
        });
    });

    return found;
};

/** Shooters who appear on this side of the match (for the player multi-select). */
export const collectMatchShotPlayerOptions = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    side: MatchShotSide
): MatchMapPlayerOption[] => {
    const byId = new Map<number, MatchMapPlayerOption>();

    shotsBySide(bundles, ourTeamIds, side).forEach((item) => {
        if (item.shooterPlayerId == null) return;
        const lineup = indexLineup(
            bundles.find((b) => b.detail.id === item.bundleId) || bundles[0]
        );
        const mpId = item.event.shot?.takenById;
        const row = mpId != null ? lineup.get(mpId) : undefined;
        if (!row || isPlaceholderPlayerName(row.name)) return;
        if (byId.has(row.playerId)) return;
        const cap = row.cap != null ? `#${row.cap} ` : '';
        byId.set(row.playerId, { playerId: row.playerId, label: `${cap}${row.name}`.trim() });
    });

    return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
};

export const collectTeamShooterZoneStats = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    side: MatchShotSide,
    options: MatchMapShotOptions = {}
): ShooterZoneStats => {
    const cells = emptyGoalCells();
    const poolCells = emptyPoolCells();
    let shots = 0;
    let goals = 0;
    let misses = 0;
    let located = 0;
    let total = 0;

    shotsBySide(bundles, ourTeamIds, side, options).forEach((item) => {
        const shot = item.event.shot!;
        const isGoal = !!shot.isGoal;
        total += 1;
        shots += 1;
        if (isGoal) goals += 1;
        else misses += 1;

        const goalZone = zoneIdForTarget(shot.targetX, shot.targetY);
        if (goalZone != null && goalZone !== 'offTarget') {
            located += 1;
            bumpGoal(cells, goalZone, isGoal);
        }

        const poolZone = poolZoneIdForLocation(shot.locationX, shot.locationY);
        if (poolZone != null && poolZone !== 'deep') {
            bumpPool(poolCells, poolZone, isGoal);
        }
    });

    return {
        cells,
        poolCells,
        shots,
        goals,
        misses,
        shootPct: shots > 0 ? Math.round((goals / shots) * 100) : undefined,
        sampleWarning: shots > 0 && shots < PLAYER_SHOT_SAMPLE_WARNING,
        located,
        total,
    };
};

const pushShotMap = (
    side: MapSide,
    item: TeamShot,
    kind: 'target' | 'location',
    detail: MatchBundle['detail'],
    names: Map<number, string>,
    scoreAt: Map<number, string>,
    finalScore?: string
): void => {
    const shot = item.event.shot!;
    side.total += 1;
    const x = kind === 'target' ? shot.targetX : shot.locationX;
    const y = kind === 'target' ? shot.targetY : shot.locationY;
    if (x == null || y == null || x < 0 || x > 1 || y < 0 || y > 1) return;
    const outcome = shotOutcome(item.event);
    const ctx = mapPointContext(
        detail,
        item.event,
        outcome === 'goal'
            ? 'Goal'
            : outcome === 'saved'
            ? 'Saved'
            : outcome === 'blocked'
            ? 'Blocked'
            : 'Miss',
        names,
        scoreAt.get(item.event.id),
        finalScore
    );
    side.points.push({
        key: `${item.bundleId}-${item.event.id}-${kind === 'target' ? 't' : 'l'}`,
        x,
        y,
        outcome,
        ...ctx,
    });
};

export const mapTeamTargets = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    side: MatchShotSide,
    options: MatchMapShotOptions = {}
): MapSide => {
    const map = emptyMapSide();
    bundles.forEach((bundle) => {
        const names = nameByMatchPlayerId(bundle.detail);
        const scoreAt = buildScoreAtMomentByEventId(bundle.detail, bundle.events);
        const finalScore = matchFinalScoreLine(bundle.detail, bundle.events);
        shotsBySide([bundle], ourTeamIds, side, options).forEach((item) =>
            pushShotMap(map, item, 'target', bundle.detail, names, scoreAt, finalScore)
        );
    });
    return map;
};

export const mapTeamLocations = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    side: MatchShotSide,
    options: MatchMapShotOptions = {}
): MapSide => {
    const map = emptyMapSide();
    bundles.forEach((bundle) => {
        const names = nameByMatchPlayerId(bundle.detail);
        const scoreAt = buildScoreAtMomentByEventId(bundle.detail, bundle.events);
        const finalScore = matchFinalScoreLine(bundle.detail, bundle.events);
        shotsBySide([bundle], ourTeamIds, side, options).forEach((item) =>
            pushShotMap(map, item, 'location', bundle.detail, names, scoreAt, finalScore)
        );
    });
    return map;
};

const exclusionOutcome = (ours: boolean): Extract<MapOutcome, 'exclusionDrew' | 'exclusionGot'> =>
    ours ? 'exclusionGot' : 'exclusionDrew';

/** We drew = opp excluded; We got = our player excluded. */
const exclusionLabel = (ours: boolean, type: string | undefined): string => {
    const side = ours ? 'We got' : 'We drew';
    if (isPenaltyExclusion(type)) return `${side} · Penalty`;
    return side;
};

/** Distinct exclusion types present in the sample (for the type multi-select). */
export const collectMatchExclusionTypeOptions = (
    bundles: MatchBundle[]
): { value: string; label: string }[] => {
    const keys = new Set<string>();
    bundles.forEach((bundle) => {
        bundle.events.forEach((event: EventLightVm) => {
            if (!event.exclusion) return;
            keys.add(normalizeExclusionTypeKey(event.exclusion.type));
        });
    });
    return Array.from(keys)
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({ value, label: formatExclusionTypeLabel(value) }));
};

/** Home / away team options for exclusion team filter (team that was excluded). */
export const collectMatchExclusionTeamOptions = (
    bundles: MatchBundle[]
): { value: number; label: string }[] => {
    const byId = new Map<number, string>();
    bundles.forEach((bundle) => {
        const home = bundle.detail.homeTeamId;
        const away = bundle.detail.awayTeamId;
        if (home != null) {
            byId.set(home, bundle.detail.homeTeamDisplayName || 'Team 1');
        }
        if (away != null) {
            byId.set(away, bundle.detail.awayTeamDisplayName || 'Team 2');
        }
    });
    return Array.from(byId.entries()).map(([value, label]) => ({ value, label }));
};

/** Players involved in exclusions (excluded or fouled) for the player filter. */
export const collectMatchExclusionPlayerOptions = (
    bundles: MatchBundle[],
    ourTeamIds: number[]
): MatchMapPlayerOption[] => {
    const byId = new Map<number, MatchMapPlayerOption>();

    bundles.forEach((bundle) => {
        const lineup = indexLineup(bundle);
        bundle.events.forEach((event: EventLightVm) => {
            if (!event.exclusion) return;
            [event.exclusion.excludedPlayerId, event.exclusion.fouledPlayerId].forEach((mpId) => {
                if (mpId == null) return;
                const row = lineup.get(mpId);
                if (!row || isPlaceholderPlayerName(row.name)) return;
                // Prefer our club players in the filter; still include opponent when they appear.
                if (byId.has(row.playerId)) return;
                const cap = row.cap != null ? `#${row.cap} ` : '';
                const sideTag = ourTeamIds.includes(row.teamId) ? '' : ' (opp)';
                byId.set(row.playerId, {
                    playerId: row.playerId,
                    label: `${cap}${row.name}${sideTag}`.trim(),
                });
            });
        });
    });

    return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Combined full-pool exclusions: we drew (opp excluded) + we got (we excluded).
 * Penalties marked with ring. Filters: player, period, type, team.
 */
export const mapMatchExclusionsCombined = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    options: MatchExclusionMapOptions = {}
): MapSide => {
    const map = emptyMapSide();
    const { playerIds, periodKeys, typeKeys, teamIds } = options;

    bundles.forEach((bundle) => {
        const lineup = indexLineup(bundle);
        const names = nameByMatchPlayerId(bundle.detail);
        const scoreAt = buildScoreAtMomentByEventId(bundle.detail, bundle.events);
        const finalScore = matchFinalScoreLine(bundle.detail, bundle.events);
        bundle.events.forEach((event: EventLightVm) => {
            if (!event.exclusion) return;
            if (!matchesPeriodFilter(event, periodKeys)) return;
            if (!allowsType(event.exclusion.type, typeKeys)) return;
            if (!allowsTeam(event.exclusion.teamId, teamIds)) return;

            const ours = ourTeamIds.includes(event.exclusion.teamId);

            const excluded =
                event.exclusion.excludedPlayerId != null
                    ? lineup.get(event.exclusion.excludedPlayerId)
                    : undefined;
            const fouled =
                event.exclusion.fouledPlayerId != null
                    ? lineup.get(event.exclusion.fouledPlayerId)
                    : undefined;
            if (playerIds != null) {
                const ok =
                    allowsPlayer(excluded?.playerId, playerIds) ||
                    allowsPlayer(fouled?.playerId, playerIds);
                if (!ok) return;
            }

            map.total += 1;
            const x = event.exclusion.locationX;
            const y = event.exclusion.locationY;
            if (x == null || y == null || x < 0 || x > 1 || y < 0 || y > 1) return;
            const outcome = exclusionOutcome(ours);
            const pen = isPenaltyExclusion(event.exclusion.type);
            const kind = exclusionLabel(ours, event.exclusion.type);
            const resultLine =
                !pen && event.exclusion.type ? `${kind} · ${event.exclusion.type}` : kind;
            const ctx = mapPointContext(
                bundle.detail,
                event,
                resultLine,
                names,
                scoreAt.get(event.id),
                finalScore
            );
            map.points.push({
                key: `${bundle.detail.id}-${event.id}-x`,
                x,
                y,
                outcome,
                isPenalty: pen || undefined,
                ...ctx,
            });
        });
    });

    return map;
};

export const countExclusionKinds = (
    side: MapSide
): { drew: number; got: number; penalty: number } => {
    let drew = 0;
    let got = 0;
    let penalty = 0;
    side.points.forEach((p) => {
        if (p.outcome === 'exclusionDrew') drew += 1;
        else if (p.outcome === 'exclusionGot') got += 1;
        if (p.isPenalty) penalty += 1;
    });
    return { drew, got, penalty };
};
